<template>
  <div class="view" v-if="canEdit || canDelete" v-cloak>
    <GridPanelHeader :title="title" :grid-ref="$refs[seasonsGridRefKey]" :show-add="canEdit" :show-filter="false" @add="newSeason" />

    <div class="unassigned-alert alert alert-warning mt-2" v-if="apiConfig && unassignedRows.length > 0">
      <img src="~@/assets/images/desktop/icons/controls/alert.svg" class="icon-svg text-dark pr-2" />
      <span
        @click="syncSeasons"
        v-html="
          $t('works.work_machines.you_have_unassigned_seasons_link', {
            count: unassignedRows.length,
          })
        "
      />
      <div class="load text-center" v-if="syncLoading">
        <loader :loading="syncLoading" size="sm" />
      </div>
    </div>

    <div class="content-body pt-1">
      <div class="row">
        <div id="toast-msg"></div>
      </div>
      <DxDataGrid
        :ref="seasonsGridRefKey"
        :data-source="seasonsDataSource"
        :column-min-width="110"
        style="min-height: 350px"
        :show-column-lines="true"
        :show-borders="true"
        :column-auto-width="true"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :row-alternation-enabled="true"
        :remote-operations="gridRemoteOperations"
        :element-attr="gridAttributes"
        @editor-preparing="onEditorPreparing"
        @exporting="onExporting"
      >
        <DxColumn
          data-field="name"
          :caption="$t('common.name')"
          data-type="string"
          :allow-sorting="false"
          :filter-operations="['contains']"
        >
          <DxRequiredRule :message="$t('common.required')" />
          <!--
          <DxPatternRule
            pattern="^\\d+\\/\\d+$"
            :message="$t('works.season.name_format')"
          />
          -->
        </DxColumn>
        <DxColumn
          data-field="year"
          :caption="$t('year')"
          alignment="left"
          data-type="number"
          :allow-editing="false"
          :allow-sorting="false"
          :filter-operations="['=']"
        >
          <DxRequiredRule :message="$t('works.season.name_format')" />
        </DxColumn>
        <DxColumn
          data-field="type"
          :caption="$t('works.season.type')"
          data-type="string"
          :allow-sorting="false"
          :filter-operations="['=']"
        >
          <DxLookup :data-source="seasonTypeOptions" value-expr="id" display-expr="label" />
          <DxRequiredRule :message="$t('common.required')" />
        </DxColumn>
        <DxColumn
          data-field="remote_id"
          :caption="$t('remote_id')"
          data-type="string"
          :allow-sorting="false"
          :filter-operations="['contains']"
        />
        <DxColumn
          data-field="created_at"
          :caption="$t('common.created_at')"
          data-type="date"
          :format="getDxGridDefaultDateFormat + ' HH:mm'"
          :allow-sorting="false"
          :allowEditing="false"
          :filter-operations="['contains']"
          :formItem="{
            visible: false,
          }"
        />
        <DxColumn
          data-field="updated_at"
          :caption="$t('common.last_update')"
          data-type="date"
          :allowEditing="false"
          :format="getDxGridDefaultDateFormat + ' HH:mm'"
          :allow-sorting="false"
          :filter-operations="['contains']"
          :formItem="{
            visible: false,
          }"
        />

        <DxColumn
          :width="60"
          alignment="center"
          :fixed="true"
          fixed-position="right"
          :allow-sorting="false"
          :allow-filtering="false"
          :show-in-column-chooser="false"
          cell-template="dynamicMenuCell"
          :formItem="{
            visible: false,
          }"
        />
        <template #dynamicMenuCell="{ data }">
          <DxActionMenu
            :grid-ref="$refs[seasonsGridRefKey]"
            row-key="uuid"
            :row="data.data"
            :can-edit="canEdit"
            :can-delete="canDelete"
            :allow-deleting="allowDeleting"
          />
        </template>

        <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" :use-icons="true" mode="form">
          <DxTexts
            :confirm-delete-message="
              $t('common.confirm_delete', {
                title: $t('common.season'),
              })
            "
            :confirm-delete-title="$t('common.confirm_delete_title')"
            :deleteRow="$t('common.delete')"
          />
        </DxEditing>

        <DxPaging :total="3500" :page-size="15" />
        <DxPager
          :visible="true"
          :allowed-page-sizes="pageSizes"
          display-mode="compact"
          :info-text="$t('common.page_info')"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
        />
        <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
        <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
        <DxColumnChooser :enabled="false" mode="select" />
        <DxColumnFixing :enabled="true" />
        <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'Seasons'" />
        <DxScrolling column-rendering-mode="virtual" />
      </DxDataGrid>
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { canDelete, canEdit, permissions, permissionTypes } from "@/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import { handleError } from "@/helpers/generalActions";
import { types } from "@/helpers/ImportTypes";
import { sourceTypes } from "@/helpers/ImportSourceTypes";
import Loader from "@/components/Loader.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import CustomStore from "devextreme/data/custom_store";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";
import {
  DxColumn,
  DxDataGrid,
  DxEditing,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxLookup,
  DxPatternRule,
  DxRequiredRule,
  DxTexts,
  DxScrolling,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
} from "devextreme-vue/data-grid";

export default {
  name: "Seasons",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      seasonsGridRefKey: "seasonsGrid",
      filterPanelVisible: false,
      syncLoading: false,
      pageSizes: [15, 25, 100],
      lastLoadedItems: [],
      seasonsDataSource: new CustomStore({
        key: "uuid",
        load: this.load,
        insert: this.insert,
        update: this.update,
        remove: this.remove,
      }),
      gridRemoteOperations: {
        paging: false,
        filtering: false,
        sorting: false,
      },
    };
  },
  components: {
    Loader,
    Forbidden,
    GridPanelHeader,
    GridFilter,
    DxActionMenu,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxEditing,
    DxLookup,
    DxPatternRule,
    DxRequiredRule,
    DxTexts,
    DxFilterRow,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxExport,
    DxScrolling,
  },
  computed: {
    ...mapGetters({
      importConfigurations: "importConfig/importConfigurations",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    title() {
      return this.$t("common.season");
    },
    seasonTypeOptions() {
      return [
        { id: "primary", label: this.$t("works.season.primary") },
        { id: "secondary", label: this.$t("works.season.secondary") },
      ];
    },
    gridAttributes() {
      return {
        class: "smagria-grid seasons-grid" + (this.apiConfig && this.unassignedRows.length > 0 ? " with-alert" : ""),
      };
    },
    config() {
      return {
        permission: permissions.SEASONS,
        permission_type: permissionTypes.access,
        edit: {
          url: "/works/seasons/{uuid}",
        },
      };
    },
    unassignedRows() {
      if (Array.isArray(this.lastLoadedItems)) {
        return this.lastLoadedItems.filter((item) => !item.remote_id);
      }
      return [];
    },
    apiConfig() {
      return this.importConfigurations?.find(
        (config) =>
          config.import_type === "SGPS_DASC_IMPORT" &&
          config.is_active === 1 &&
          config.source_type === "API_IMPORT" &&
          config.source_config_uuid,
      );
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getSeasons: "seasons/getSeasons",
      createSeason: "seasons/createSeason",
      updateSeason: "seasons/updateSeason",
      deleteSeason: "seasons/deleteSeason",
      syncTracktorApiProjects: "importConfig/syncTracktorApiProjects",
      getImportConfigurations: "importConfig/getImportConfigurations",
    }),
    allowUpdating() {
      return this.canEdit;
    },
    allowDeleting() {
      return this.canDelete;
    },
    onEditorPreparing(e) {
      if (e.parentType !== "dataRow" || e.dataField !== "name") {
        return;
      }

      const baseOnValueChanged = e.editorOptions.onValueChanged;

      e.editorOptions.onValueChanged = (args) => {
        if (baseOnValueChanged) {
          baseOnValueChanged(args);
        }

        const nextYear = this.getSeasonNextYear(args.value);
        e.component.cellValue(e.row.rowIndex, "year", nextYear);
      };
    },
    async load(loadOptions) {
      let dataSource = {
        data: [],
        totalCount: 0,
      };

      const payload = {
        limit: 99999,
      };

      await this.getSeasons(payload).then((response) => {
        const rawItems = Array.isArray(response.data) ? response.data : [];
        const displayItems = rawItems.map((item) => ({
          ...item,
          name: item.name ? item.name.split(' (')[0] : item.name,
        }));

        dataSource.data = displayItems;
        this.lastLoadedItems = rawItems;
        dataSource.totalCount = this.pagination?.total ?? this.lastLoadedItems.length;
      });

      return dataSource;
    },
    async insert(values) {
      const payload = this.normalizeSeasonPayload(values);
      return this.createSeason(payload);
    },
    async update(key, values) {
      const baseItem = this.getSeasonBaseItem(key);
      const payload = this.normalizeSeasonPayload({ ...baseItem, ...values, uuid: key });
      return this.updateSeason(payload);
    },
    async remove(key) {
      await this.deleteSeason({
        uuid: key,
      });
    },
    normalizeSeasonPayload(values) {
      const payload = { ...values };
      const nextYear = this.getSeasonNextYear(payload.name);

      if (nextYear) {
        payload.year = nextYear;
      }

      payload.type = payload.type || "primary";

      return payload;
    },
    getSeasonBaseItem(key) {
      if (!Array.isArray(this.lastLoadedItems)) {
        return {};
      }

      return this.lastLoadedItems.find((item) => item.uuid === key) || {};
    },
    getSeasonNextYear(seasonName) {
      if (!seasonName) {
        return null;
      }

      const seasonNamePattern = /^\d+\/\d+$/;
      if (!seasonNamePattern.test(seasonName)) {
        return null;
      }

      const parts = seasonName.split("/");
      return parts[1] || null;
    },
    newSeason() {
      const grid = this.$refs[this.seasonsGridRefKey];
      if (grid && grid.instance) {
        grid.instance.addRow();
      }
    },
    syncSeasons(event) {
      const vm = this;
      if (event.target.classList.contains("clickable")) {
        vm.syncLoading = true;
        this.syncTracktorApiProjects({
          import_configuration_uuid: this.apiConfig["uuid"] ?? null,
        })
          .then(() => {
            vm.$refs[vm.seasonsGridRefKey]?.instance?.refresh();
          })
          .catch((e) => {
            handleError(vm, e);
          })
          .finally(() => {
            vm.syncLoading = false;
          });
      }
    },
    onExporting(e) {
      e.fileName = "Seasons_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Seasons");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
  },
  mounted() {
    this.getImportConfigurations({
      import_type: types.SGPS_DASC_IMPORT.id,
      source_type: sourceTypes.API_IMPORT.id,
    });
  },
};
</script>
<style scoped lang="scss">
.seasons-grid.with-alert {
  /* reduce height when the alert box is visible */
  height: calc(95vh - 210px);
}
</style>
